'use client';

import { useEffect, useState } from 'react';

/**
 * Hook to detect user's reduced motion preference
 */
export function useReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    // Set initial value
    setPrefersReducedMotion(mediaQuery.matches);

    // Listen for changes
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    // Use the newer addEventListener if available, fallback to addListener
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        // Fallback for older browsers
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  return prefersReducedMotion;
}

/**
 * Hook to get animation variants based on reduced motion preference
 */
export function useAnimationVariants() {
  const prefersReducedMotion = useReducedMotion();

  const getVariants = (originalVariants: any) => {
    if (prefersReducedMotion) {
      // Return static variants for reduced motion
      return {
        hidden: { opacity: 0 },
        visible: { 
          opacity: 1,
          transition: { duration: 0.2 } // Very quick fade-in only
        }
      };
    }
    return originalVariants;
  };

  const getTransition = (originalTransition: any) => {
    if (prefersReducedMotion) {
      return { duration: 0.2 }; // Quick transitions only
    }
    return originalTransition;
  };

  return {
    prefersReducedMotion,
    getVariants,
    getTransition
  };
}